<template>
  <section id="home" class="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 sm:pt-24 md:pt-20">
    <!-- Background Video/Image -->
    <div class="absolute inset-0 z-0">
      <!-- Background Layer 1 (Base Layer) -->
      <div
        class="absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-200"
        :style="{
          backgroundImage: 'url(' + currentBackgroundImage + ')',
          opacity: isTransitioning ? 0 : 1
        }"
      ></div>

      <!-- Background Layer 2 (Transition Layer) -->
      <div
        class="absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-200"
        :style="{
          backgroundImage: 'url(' + nextBackgroundImage + ')',
          opacity: isTransitioning ? 1 : 0
        }"
      ></div>

      <!-- Gradient Overlay -->
      <div class="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent"></div>
    </div>

    <!-- Content -->
    <div class="relative z-10 container-custom text-center text-white">
      <div class="max-w-4xl mx-auto">
        <!-- Main Title -->
        <h1
          class="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 sm:mb-6 animate-fade-in px-4 sm:px-0"
        >
          <span class="block text-gradient bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
            {{ $t('hero.title') }}
          </span>
        </h1>

        <!-- Subtitle -->
        <p
          class="text-lg sm:text-xl md:text-2xl lg:text-3xl font-light leading-relaxed mt-8 mb-6 sm:mb-8 text-gray-200 animate-slide-up px-4 sm:px-0"
          v-scroll-reveal="{ delay: 200 }"
        >
          {{ $t('hero.subtitle') }}
        </p>

        <!-- CTA Buttons -->
        <!-- <div
          class="flex flex-col sm:flex-row gap-4 justify-center items-center animate-slide-up"
          v-scroll-reveal="{ delay: 400 }"
        >
          <button
            @click="scrollToSection('wherever')"
            class="btn-primary text-lg px-8 py-4 hover:scale-105 transition-transform"
          >
            {{ $t('hero.ctaPrimary') }}
          </button>
          <button
            @click="scrollToSection('contact')"
            class="btn-outline text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-gray-900"
          >
            {{ $t('hero.ctaSecondary') }}
          </button>
        </div> -->

        <!-- Key Statistics (optional) -->
        <!-- <div 
          class="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 animate-slide-up"
          v-scroll-reveal="{ delay: 600 }"
        >
          <div class="text-center">
            <div class="text-3xl md:text-4xl font-bold text-white mb-2">10+</div>
            <div class="text-gray-300">{{ $t('features.items[0]') }}</div>
          </div>
          <div class="text-center">
            <div class="text-3xl md:text-4xl font-bold text-white mb-2">500+</div>
            <div class="text-gray-300">{{ $t('projects.viewAll') }}</div>
          </div>
          <div class="text-center">
            <div class="text-3xl md:text-4xl font-bold text-white mb-2">95%</div>
            <div class="text-gray-300">{{ $t('services.title') }}</div>
          </div>
          <div class="text-center">
            <div class="text-3xl md:text-4xl font-bold text-white mb-2">24/7</div>
            <div class="text-gray-300">{{ $t('footer.backToTop') }}</div>
          </div>
        </div> -->
      </div>
    </div>

    <!-- Scroll Indicator -->
    <div class="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
      <div class="flex flex-col items-center text-white animate-bounce">
        <span class="text-sm mb-2">{{ $t('common.readMore') }}</span>
        <svg
          class="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 14l-7 7m0 0l-7-7m7 7V3"
          ></path>
        </svg>
      </div>
    </div>

  </section>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLanguageStore } from '@/stores/language'

export default {
  name: 'HeroSection',
  setup() {
    const { t } = useI18n()
    const languageStore = useLanguageStore()
    const showVideo = ref(false) // Set to true to enable background video

    // Background images carousel
    const carouselImages = [
      '/images/hero-bg-01.jpg',
      '/images/hero-bg-02.jpg',
      '/images/hero-bg-03.jpg',
      '/images/hero-bg-04.jpg',
      '/images/hero-bg-05.jpg'
    ]

    const currentImageIndex = ref(0)
    const carouselInterval = ref(null)
    const isTransitioning = ref(false)
    const nextImageIndex = ref(1) // Initialize with next image index

    const currentBackgroundImage = computed(() => {
      return carouselImages[currentImageIndex.value]
    })

    const nextBackgroundImage = computed(() => {
      return carouselImages[nextImageIndex.value]
    })

    // Start carousel
    const startCarousel = () => {
      carouselInterval.value = setInterval(() => {
        // Start transition by showing the next image layer
        isTransitioning.value = true

        // After transition completes, swap the images
        setTimeout(async () => {
          // Update current image to what was just shown in transition layer
          currentImageIndex.value = nextImageIndex.value
          // Switch visibility (Base layer fades in, Transition layer fades out)
          isTransitioning.value = false
          // Wait for DOM update before updating next image (so transition layer updates while fading out)
          await nextTick()
          nextImageIndex.value = (nextImageIndex.value + 1) % carouselImages.length
        }, 1) // Match the CSS transition duration (0.001 seconds)
      }, 3500) // Change every 3.5 seconds
    }

    // Stop carousel
    const stopCarousel = () => {
      if (carouselInterval.value) {
        clearInterval(carouselInterval.value)
        carouselInterval.value = null
      }
    }

    const currentLanguage = computed(() => languageStore.currentLanguage)
    const availableLanguages = computed(() => languageStore.availableLanguages)

    // Language functions
    const setLanguage = (languageCode) => {
      languageStore.setLanguage(languageCode)
    }

    // Scroll to section function
    const scrollToSection = (sectionId) => {
      const element = document.getElementById(sectionId)
      if (element) {
        const headerHeight = 80
        const elementPosition = element.offsetTop - headerHeight
        
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        })
      }
    }

    // Auto-scroll to next section on scroll indicator click
    const handleScrollIndicatorClick = () => {
      scrollToSection('about')
    }

    // Initialize scroll animations and carousel
    onMounted(() => {
      // Start background image carousel
      startCarousel()

      // Add scroll reveal animations
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated')
          }
        })
      }, observerOptions)

      // Observe all elements with v-scroll-reveal
      document.querySelectorAll('[v-scroll-reveal]').forEach(el => {
        observer.observe(el)
      })
    })

    // Cleanup carousel on unmount
    onUnmounted(() => {
      stopCarousel()
    })

    return {
      showVideo,
      currentBackgroundImage,
      nextBackgroundImage,
      isTransitioning,
      currentLanguage,
      availableLanguages,
      setLanguage,
      scrollToSection,
      handleScrollIndicatorClick,
      startCarousel,
      stopCarousel
    }
  }
}
</script>

<style scoped>
/* Hero section specific styles */
#home {
  position: relative;
  background-attachment: fixed;
  background-size: cover;
  background-position: center;
}

/* Background video specific styles */
video {
  opacity: 0.7;
}

/* Text gradient animation */
.text-gradient {
  background-size: 200% auto;
  animation: gradientShift 3s ease infinite;
}

@keyframes gradientShift {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

/* Scroll reveal animations */
[v-scroll-reveal] {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

[v-scroll-reveal].animated {
  opacity: 1;
  transform: translateY(0);
}

/* Button hover effects */
.btn-primary,
.btn-outline {
  backdrop-filter: blur(4px);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  #home {
    background-attachment: scroll;
  }
  
  .text-gradient {
    font-size: 2.5rem;
  }
}

/* Background image styling */
.bg-cover {
  background-size: cover;
  background-position: center;
}

/* Smooth transitions for language switching */
.lang-transition {
  transition: all 0.3s ease;
}
</style>
